import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useShuffleArenaOfferMutation } from '@/api/queries/shop';
import { playSound } from '@/common/lib/soundEffect';
import { type ArenaOffersDto } from '@/common/types/shop/marketBoxes';
import GlimmerAnimation from '@/components/Animations/GlimmerAnimation/GlimmerAnimation';
import { Ticker } from '@/components/common/Ticker/Ticker';
import {
  arenaOffersInfo,
  type ProfitDropRewardType,
} from '@/components/OffersMarket/ArenaOffers/config';
import ProfitDropRewardCard from '@/components/OffersMarket/ArenaOffers/ProfitDrop/Rewards/ProfitDropRewardCard';
import RewardArray from '@/components/OffersMarket/ArenaOffers/RewardArray';
import { H2, StringSmall } from '@/components/Typography/Typography';
import { GreyBlurSmButton } from '@/components/ui/Button/Button';
import { BuyForTgStarButton } from '@/components/ui/BuyForTgStarButton/BuyForTgStarButton';
// import RadialCircleGradient from '@/components/ui/Gradient/RadialCircleGradient';
import { Picture } from '@/components/ui/Picture/Picture';
import ArenaOfferTimer from '@/components/ui/Timer/ArenaOfferTimer';
import { changeProfitDropOffer } from '@/redux/reducers/shopReducer';
import { useAppDispatch } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import css from './style.module.scss';

import superStartImg from '/img/shop/arenaOffers/superStartIcon.svg';
import arrows from '/img/shop/boxes/arrows.svg';

interface prop {
  arenaOffer: ArenaOffersDto;
}

const PopupCard: React.FC<prop> = ({ arenaOffer }) => {
  const { id, name, reward, cost, discountTimeLeftInSeconds } = arenaOffer;
  const info = arenaOffersInfo[name];
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { secondModal } = useContext(ModalContext);
  const [shuffle, { isLoading }] = useShuffleArenaOfferMutation();
  const isDiscount: boolean = cost.has_discount;
  const prices = isDiscount ? cost.discounted : cost.base;
  const isSuperStart = name === 'superStart';
  const isWeekend = [
    'growthKitSmall',
    'growthKitBig',
    'growthKitGiant',
    'profitDropLarge',
    'profitDropSmall',
  ].includes(name);

  const isProfitDrop = name === 'profitDropLarge' || name === 'profitDropSmall';

  const onBuyOffer = (): void => {
    if (isProfitDrop) {
      dispatch(changeProfitDropOffer(arenaOffer));
    }
    secondModal.openModal({
      renderKind: 'buyArenaOffer',
      props: { arenaOffer },
    });
  };

  return (
    <div
      className={cn(
        css.arenaOffer,
        info.color && css[info.color],
        isProfitDrop && 'pt-[max(270px,50%)]',
      )}
    >
      <div className="!absolute w-[45%] z-[10] -top-[10px] left-1/2 -translate-x-1/2">
        <Ticker
          variant="ticker-center"
          extraVariant={
            info.color && (info.color === 'yellow' ? 'gold' : info.color)
          }
          text={isDiscount ? info.ticker.discount : info.ticker.default}
          classnamestext="text-[14px]"
          classnamesinblock="justify-center"
        />
        {isDiscount && (
          <ArenaOfferTimer
            text={isSuperStart ? 'for 1-6 lvl' : undefined}
            value={discountTimeLeftInSeconds}
            variant="bottom"
            classNames="!text-[16px]"
          />
        )}
      </div>
      <div className={cn('flex flex-col gap-[20px]', isWeekend && 'mt-[30px]')}>
        <div className="text-center">
          <H2 className="mb-[4px]">{t(info.title)}</H2>
          <div className="w-full flex items-center justify-center">
            <StringSmall
              className={cn('!leading-[1.2] !text-[12px]', {
                '!text-[#b8ffb6]': info.color === 'green',
                '!text-[#fffdb6]': info.color === 'yellow',
                '!text-[#ffb6b6]': info.color === 'red',
                '!text-[#E7D7FF]': info.color === 'purple',
              })}
            >
              {t(info.describe)}
            </StringSmall>
          </div>
        </div>
        {isProfitDrop ? (
          <div className="gap-2 flex h-16 justify-center mb-4 mt-3">
            {Object.keys(reward).map((key) => (
              <ProfitDropRewardCard
                key={key}
                type={key as ProfitDropRewardType}
                value={reward[key as ProfitDropRewardType]}
              />
            ))}
          </div>
        ) : (
          <RewardArray
            variant={Object.keys(reward).length > 3 ? 'big' : 'huge'}
            offerId={id}
            name={name}
            reward={reward}
            classNames="items-center"
          />
        )}
        {!isWeekend && (
          <StringSmall className="text-center !text-[#AAAAAA] -mt-[10px]">
            {t('Tap to keep 1 component')}
          </StringSmall>
        )}
        <div className="flex gap-[16px] relative z-[5] !h-[40px]">
          {!isWeekend && (
            <div className="relative">
              <div className="absolute w-full h-full top-0 left-0 overflow-hidden rounded-[8px] z-[-1]">
                <GlimmerAnimation />
              </div>
              <GreyBlurSmButton
                className="h-full !w-[88px]"
                text=""
                isLoading={isLoading}
                onClick={() => {
                  playSound('buttons/shuffle');
                  shuffle({ id, body: reward });
                }}
              >
                <img src={arrows} alt="arrows" className="w-[23px]" />
              </GreyBlurSmButton>
            </div>
          )}
          <div className="relative h-full w-full">
            <BuyForTgStarButton
              className="h-full"
              price={isDiscount && !isSuperStart ? cost.base.usd : undefined}
              discountPercentage={
                isDiscount && !isSuperStart
                  ? cost.displayDiscountPct
                  : undefined
              }
              discountPrice={prices.usd}
              wrapperClassName="w-full h-full"
              onClick={onBuyOffer}
            />
          </div>
        </div>
      </div>
      {isProfitDrop && (
        <Picture
          img={info.img.png}
          webp={info.img.webp}
          className="h-[13.5rem] ml-4 absolute top-16 w-full [&>*]:object-scale-down left-1/2 -translate-x-1/2 z-10 "
          alt="banner"
        />
      )}
      <div
        className={cn(
          css.shadowBlock,
          info.color && css[info.color],
          'absolute top-0 left-0 w-full h-full overflow-hidden rounded-[8px] bg-[#000] z-[-2]',
        )}
      >
        {isSuperStart && (
          <div className="absolute top-[17%] right-[15%] z-[2] w-[80px] h-[80px]">
            <img src={superStartImg} className="w-full h-full" alt="icon" />
            <StringSmall className="absolute top-1/2 -translate-y-1/2 !font-[900] !text-[12px] w-full text-center rotate-[15deg]">
              {cost.value} %<br /> VALUE
            </StringSmall>
          </div>
        )}
        <Picture
          img={info.imgPopup.png}
          webp={info.imgPopup.webp}
          className={cn(
            css.imgShadow,
            'absolute top-0 right-0 w-full aspect-ratio-1/1 z-[1]',
          )}
          alt="banner"
        />
        {/* <RadialCircleGradient
          type={info.color}
          className="bottom-[-30%] left-[50%] -translate-x-1/2 w-[200%] h-[50%] aspect-ratio-2/1 opacity-[0.5] !backdrop-blur-0 !blur-0 z-[2]"
        /> */}
        {/* <RadialCircleGradient
          type={info.color}
          className="top-[-50%] left-[-50%] translate-x-1/2 w-[50%] aspect-ratio-1/1 opacity-[0.5] !backdrop-blur-0 !blur-0 z-[2]"
        />
        <RadialCircleGradient
          type={info.color}
          className="top-[-50%] right-[-50%] -translate-x-1/2 w-[50%] aspect-ratio-1/1 opacity-[0.5] !backdrop-blur-0 !blur-0 z-[2]"
        /> */}
      </div>
    </div>
  );
};

export default PopupCard;
